// src/components/InfrastructureGallery.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const InfrastructureGallery = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  // All Facilities Data ── only unique entries
  const facilities = [
    {
      id: 2,
      category: 'Computer Lab',
      image: '/images/gallery/biology-1.png',
      title: 'Advanced Computer Laboratory',
      description: 'High-speed internet and latest software for digital learning.',
    },
    {
      id: 12,
      category: 'Physics Lab',
      image: '/images/gallery/physics-6.png',
      title: 'Fully Equipped Physics Laboratory',
      description: 'Hands-on experimental learning with modern apparatus, instruments, and strict safety protocols.',
    },
    {
      id: 3,
      category: 'Chemistry Lab',
      image: '/images/gallery/biology-10.png',
      title: 'Advanced Chemistry Laboratory',
      description: 'Well-ventilated laboratory with modern equipment, chemical safety systems, and supervised practical work.',
    },
    {
      id: 11,
      category: 'Biology Lab',
      image: '/images/gallery/biology-12.png',
      title: 'Comprehensive Biology Laboratory',
      description: 'Fully equipped lab with microscopes, specimens, models, and biosafety measures for practical learning.',
    },
    
    {
      id: 4,
      category: 'Maths Lab',
      image: '/images/gallery/mathematics-1.png',
      title: 'Interactive Mathematics Lab',
      description: 'Hands-on learning tools to make math fun and engaging.',
    },
    {
      id: 5,
      category: 'Library',
      image: '/images/gallery/library-1.png',
      title: 'Rich & Quiet Library',
      description: 'Over 10,000 books, journals, and digital reading resources.',
    },
    {
      id: 6,
      category: 'AI & Robotics',
      image: '/images/gallery/robotics-03.png',
      title: 'AI & Robotics Lab',
      description: 'Dedicated space for coding, robotics, AI projects and innovation.',
    },
    {
      id: 7,
      category: 'Music Room',
      image: '/images/gallery/music-room-1.png',
      title: 'Dedicated Music Room',
      description: 'Equipped with musical instruments and soundproofing.',
    },
    {
      id: 8,
      category: 'Smart Class',
      image: '/images/gallery/digital_class_room.png',
      title: 'Digital Smart Classrooms',
      description: 'Interactive panels and audio-visual teaching aids.',
    },
    {
      id: 9,
      category: 'Transportation',
      image: '/images/gallery/transport-1.png',
      title: 'Safe School Transportation',
      description: 'GPS-enabled buses with trained drivers and attendants.',
    },
    {
      id: 10,
      category: 'Safety & Security',
      image: '/images/gallery/security.png',
      title: '24/7 Safety & CCTV Surveillance',
      description: 'Complete campus under CCTV coverage with trained security staff.',
    },
    {
      id: 13,
      category: 'Art & Craft Room',
      image: '/images/art/art-1.png',
      title: 'Creative Art & Craft Room',
      description: 'Encouraging creativity and imagination through drawing, painting, and craft activities.',
    },

    {
      id: 14,
      category: 'Geography Lab',
      image: '/images/gallery/geography1.png',
      title: 'Geography Lab',
      description: 'The Geography Lab is an important learning space where students understand geographical concepts..',
    },
  ];

  const handleFilterClick = (filter) => {
    setActiveFilter(filter);
  };

  // Improved filter logic
  const filteredFacilities = (() => {
    if (activeFilter === 'all') {
      return facilities;
    }

    if (activeFilter === 'Science Lab') {
      return facilities.filter(f => 
        f.category === 'Chemistry Lab' || 
        f.category === 'Biology Lab' || 
        f.category === 'Physics Lab'
      );
    }

    // Default: exact category match (case-insensitive)
    return facilities.filter(f => 
      f.category.toLowerCase() === activeFilter.toLowerCase()
    );
  })();

  // Clean slug generator
  const createSlug = (title) => {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  return (
    <section className="space-ptb bg-light">
      <div className="container">
        <div className="row justify-content-between align-items-center mb-0">
          <div className="col-lg-12">
            <div className="section-title text-center">
              <h2 className="title mb-1">Our Infrastructure</h2>
              <p className="mb-0">Explore our world-class facilities designed for holistic learning</p>
            </div>
          </div>
        </div>

        {/* Filter Buttons */}
        <div className="row mb-5">
          <div className="col-12">
            <div className="filters-group text-center">
              {[
                'all',
                'Computer Lab',
                'Science Lab',
                'Maths Lab',
                'Library',
                'AI & Robotics',
                'Music Room',
                'Smart Class',
                'Transportation',
                'Safety & Security',
                'Art & Craft Room',
                'Geography Lab'
              ].map((filter) => (
                <button
                  key={filter}
                  className={`btn btn-outline-primary btn-filter mx-1 mb-2 ${activeFilter === filter ? 'active bg-primary text-white' : ''}`}
                  onClick={() => handleFilterClick(filter)}
                >
                  {filter === 'all' ? 'All' : filter}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Gallery Grid */}
        <div className="row popup-gallery">
          {filteredFacilities.length > 0 ? (
            filteredFacilities.map((facility) => (
              <div key={facility.id} className="col-lg-4 col-md-6 mb-4">
                <Link
                  to={`/${createSlug(facility.title)}`}
                  className="text-decoration-none"
                >
                  <div className="infrastructure-card h-100 shadow-sm rounded overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-2 bg-white">
                    <div className="position-relative overflow-hidden">
                      <img
                        className="img-fluid w-100 transition-transform duration-500 hover:scale-110"
                        src={facility.image}
                        alt={facility.title}
                        style={{ height: '280px', objectFit: 'cover' }}
                      />
                      <div className="infrastructure-tag position-absolute top-3 start-3">
                        <span className="badge bg-primary text-white px-3 py-2">{facility.category}</span>
                      </div>
                    </div>
                    <div className="p-4">
                      <h5 className="mb-2 text-dark fw-bold">{facility.title}</h5>
                      <p className="text-muted small mb-0 line-clamp-2">{facility.description}</p>
                    </div>
                  </div>
                </Link>
              </div>
            ))
          ) : (
            <div className="col-12 text-center py-5">
              <p className="text-muted fs-5">No facilities found in this category.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default InfrastructureGallery;